// Brute-force solver that bails on the second solution. Used by the carver to
// guarantee every emitted puzzle has exactly one solution — non-unique sudoku
// is unsolvable in the spirit of the game, no matter how hard the carver's
// logical solver thinks it is.
//
// Uses bitmask-tracked candidates + the MRV ("most-constrained variable")
// heuristic: pick the empty cell with the fewest legal digits next. Combined
// with the early exit at 2 solutions, this is fast enough to call hundreds of
// times during a single carve.

import {
  BOARD_SIZE,
  BOX_SIZE,
  CELL_COUNT,
  cloneGrid,
  type Digit,
  type Grid,
  type NonEmptyDigit,
} from './types.js';

const ALL_DIGITS_MASK = 0b1111111110;

function bit(digit: NonEmptyDigit): number {
  return 1 << digit;
}

function popcount(x: number): number {
  let n = 0;
  let v = x;
  while (v !== 0) {
    v &= v - 1;
    n++;
  }
  return n;
}

interface Masks {
  readonly rows: number[];
  readonly cols: number[];
  readonly boxes: number[];
}

// Returns null when the given puzzle is already invalid (a digit repeats in a
// row/col/box). Without that check the solver would treat an illegal puzzle as
// merely "constrained" and search exponentially for a completion that can't
// exist — see the `parses an obviously-illegal board to 0 solutions` test.
function buildMasks(grid: Grid): Masks | null {
  const rows = new Array<number>(BOARD_SIZE).fill(0);
  const cols = new Array<number>(BOARD_SIZE).fill(0);
  const boxes = new Array<number>(BOARD_SIZE).fill(0);
  for (let i = 0; i < CELL_COUNT; i++) {
    const d = grid[i] ?? 0;
    if (d === 0) continue;
    const row = Math.floor(i / BOARD_SIZE);
    const col = i % BOARD_SIZE;
    const box = Math.floor(row / BOX_SIZE) * BOX_SIZE + Math.floor(col / BOX_SIZE);
    const b = bit(d as NonEmptyDigit);
    const rm = rows[row] ?? 0;
    const cm = cols[col] ?? 0;
    const bm = boxes[box] ?? 0;
    if ((rm & b) !== 0 || (cm & b) !== 0 || (bm & b) !== 0) return null;
    rows[row] = rm | b;
    cols[col] = cm | b;
    boxes[box] = bm | b;
  }
  return { rows, cols, boxes };
}

/**
 * Returns the number of solutions to `puzzle`, capped at `cap` (default 2 —
 * usually all the carver cares about). Does not mutate `puzzle`.
 */
export function countSolutions(puzzle: Grid, cap = 2): number {
  const grid = cloneGrid(puzzle);
  const masks = buildMasks(grid);
  if (masks === null) return 0;
  return search(grid, masks, cap);
}

/** Convenience wrapper for the carver's call sites. */
export function hasUniqueSolution(puzzle: Grid): boolean {
  return countSolutions(puzzle, 2) === 1;
}

function search(grid: Grid, masks: Masks, cap: number): number {
  let bestIndex = -1;
  let bestCandidates = 0;
  let bestCount = 10;
  for (let i = 0; i < CELL_COUNT; i++) {
    if ((grid[i] ?? 0) !== 0) continue;
    const row = Math.floor(i / BOARD_SIZE);
    const col = i % BOARD_SIZE;
    const box = Math.floor(row / BOX_SIZE) * BOX_SIZE + Math.floor(col / BOX_SIZE);
    const used = (masks.rows[row] ?? 0) | (masks.cols[col] ?? 0) | (masks.boxes[box] ?? 0);
    const candidates = ALL_DIGITS_MASK & ~used;
    const count = popcount(candidates);
    if (count === 0) return 0;
    if (count < bestCount) {
      bestCount = count;
      bestIndex = i;
      bestCandidates = candidates;
      if (count === 1) break;
    }
  }
  if (bestIndex === -1) return 1;

  let found = 0;
  const row = Math.floor(bestIndex / BOARD_SIZE);
  const col = bestIndex % BOARD_SIZE;
  const box = Math.floor(row / BOX_SIZE) * BOX_SIZE + Math.floor(col / BOX_SIZE);
  let cand = bestCandidates;
  while (cand !== 0) {
    const lowBit = cand & -cand;
    cand ^= lowBit;
    const digit = (31 - Math.clz32(lowBit)) as NonEmptyDigit;
    grid[bestIndex] = digit as Digit;
    masks.rows[row] = (masks.rows[row] ?? 0) | lowBit;
    masks.cols[col] = (masks.cols[col] ?? 0) | lowBit;
    masks.boxes[box] = (masks.boxes[box] ?? 0) | lowBit;
    found += search(grid, masks, cap - found);
    grid[bestIndex] = 0;
    masks.rows[row] = (masks.rows[row] ?? 0) & ~lowBit;
    masks.cols[col] = (masks.cols[col] ?? 0) & ~lowBit;
    masks.boxes[box] = (masks.boxes[box] ?? 0) & ~lowBit;
    if (found >= cap) return found;
  }
  return found;
}
